import { useMemo } from 'react';
import { useSlate } from 'slate-react';
import { LabeledSection } from './LabeledSection';
import { NewDropdown, Typography } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { useAttachFootnote, useDetachFootnote, useFootnotesByDocument, useFootnotesForTag } from 'app/api/tag-queries';
import { footnoteSnippet } from 'app/slate/extensions/tag/utils/footnote';

interface FootnotesSectionProps {
  tagId: string;
  documentSuperId: number;
  disabled?: boolean;
}

const truncate = (s: string, n = 60): string => (s.length > n ? `${s.slice(0, n)}…` : s);

export const FootnotesSection = ({ tagId, documentSuperId, disabled = false }: FootnotesSectionProps) => {
  const editor = useSlate();
  const { data: documentFootnotes } = useFootnotesByDocument(documentSuperId);
  const { data: attachedIds } = useFootnotesForTag(tagId);
  const { mutate: attach } = useAttachFootnote();
  const { mutate: detach } = useDetachFootnote();

  const labelFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const fn of documentFootnotes ?? []) {
      map.set(fn.id, footnoteSnippet(editor, fn.id) ?? fn.id);
    }
    return map;
  }, [documentFootnotes, editor]);

  if ((documentFootnotes ?? []).length === 0 && (attachedIds ?? []).length === 0) return null;

  const attached = attachedIds ?? [];
  const attachable = (documentFootnotes ?? []).filter((fn) => !attached.includes(fn.id));

  return (
    <LabeledSection label={i18n('tag.footnotes.title')}>
      {attached.map((footnoteId) => (
        <div key={footnoteId} className="flex-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <Typography variant="body-7" fontWeight="light" style={{ minWidth: 0, flex: 1, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
            {labelFor.get(footnoteId) ?? footnoteId}
          </Typography>
          {!disabled && (
            <button className="btn btn-link p-0" style={{ flexShrink: 0 }} onClick={() => detach({ footnoteId, tagId })} title={i18n('common.remove')}>
              <i className="fa-regular fa-xmark" />
            </button>
          )}
        </div>
      ))}

      {!disabled && attachable.length > 0 && (
        <NewDropdown
          fullwidth
          options={attachable.map((fn) => ({ value: fn.id, label: truncate(labelFor.get(fn.id) ?? fn.id) }))}
          currentValue={i18n('tag.footnotes.attach')}
          onUpdate={(footnoteId: string) => attach({ footnoteId, tagId })}
        />
      )}
    </LabeledSection>
  );
};
